import cx from "classnames";
import { Helmet } from "react-helmet";

import {
  StringParam,
  useQueryParam,
} from "@mywheels/common/util/useQueryParams";
import {
  GraphCMSQueryDef,
  useGraphCMS,
} from "@mywheels/common/lib/useGraphCMS";
import { useTranslation } from "@mywheels/common/lib/translation";

import { ToolbarSpacer } from "web/layout/Toolbar";
import { Markdown, Spinner, useScrollToTop } from "web/ui";
import { useBasicLightBox } from "web/ui/lightbox";

type TestAppInstructionStep = {
  id: string;
  content: string; // markdown
  screenshot: {
    url: string;
    thumb: string;
  };
};

export const GetBetaAppInstructionsQuery: GraphCMSQueryDef<
  TestAppInstructionStep[],
  { language: string; platform: string }
> = {
  key: "betaAppInstructionSteps",
  query: `
    query GetBetaAppInstructionsQuery($language: Locale!, $platform: AppPlatform!) {
      betaAppInstructionSteps(orderBy: order_ASC, where: { platform: $platform }, locales: [$language]) {
        id
        content
        screenshot {
          url
          thumb: url(transformation: {
            image: {
              resize: {
                width: 200,
                fit: max
              }
            }
          })
        }
      }
    }    
  `,
};

export default function BetaAppPage() {
  const { language } = useTranslation();

  useScrollToTop();

  const [platform = "ios", setPlatform] = useQueryParam(
    "platform",
    StringParam
  );

  const [openLightBox, lightbox] = useBasicLightBox();

  const steps = useGraphCMS(GetBetaAppInstructionsQuery, {
    language,
    platform,
  });

  return (
    <>
      <Helmet>
        <title>Instructies voor het downloaden van de test-app</title>
      </Helmet>

      {lightbox}

      <ToolbarSpacer />

      <div className="container py-10 md:py-14">
        <h1 className="mb-1 text-lg text-black text-opacity-60">
          Instructies voor het downloaden van de test-app
        </h1>
        <div className="text-4xl md:text-5xl mb-10 -mx-2 flex">
          <button
            onClick={() => setPlatform("ios")}
            className={cx(
              "px-6 py-1 rounded-full text-[#523737] transition-transform focus:outline-none active:scale-[0.95]",
              platform === "ios"
                ? "font-display bg-[#523737] bg-opacity-10"
                : "font-displaytext text-opacity-50"
            )}
          >
            iOS
          </button>
          <button
            onClick={() => setPlatform("android")}
            className={cx(
              "px-6 py-1 rounded-full text-[#523737] transition-transform focus:outline-none active:scale-[0.95]",
              platform === "android"
                ? "font-display bg-[#523737] bg-opacity-10"
                : "font-displaytext text-opacity-50"
            )}
          >
            Android
          </button>
        </div>

        {!steps.data ? (
          <Spinner />
        ) : (
          <div className="-mx-3 flex flex-wrap">
            {steps.data.map((step, i) => {
              return (
                <div key={step.id} className="p-3 w-1/2 md:w-1/3 lg:w-1/4">
                  <div className="text-black font-semibold">Stap {i + 1}.</div>
                  <div className="prose min-h-[100px]">
                    <Markdown source={step.content} />
                  </div>
                  <button onClick={() => openLightBox(step.screenshot.url)}>
                    <img
                      className="block max-w-full"
                      alt=""
                      src={step.screenshot.thumb}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
